
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingActions from '../../components/feature/FloatingActions';
import AnimatedSection from '../../components/base/AnimatedSection';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4m4tln7s74mkk8pduag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-phone-line',
      title: 'Phone',
      detail: '+1 (234) 567-890',
      link: 'tel:+1234567890',
    },
    {
      icon: 'ri-mail-line',
      title: 'Email',
      detail: 'info@medlabpro.com',
      link: 'mailto:info@medlabpro.com',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Address',
      detail: '123 Medical Plaza, Healthcare District, New York, NY 10001',
      link: '#',
    },
    {
      icon: 'ri-time-line',
      title: 'Working Hours',
      detail: 'Mon-Fri: 8:00 AM - 6:00 PM, Sat: 9:00 AM - 2:00 PM',
      link: '#',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FloatingActions />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/429155e7ddbf5c118b8bf71273492873.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-royal-900/70"></div>
        </div>

        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-5xl lg:text-6xl font-display font-bold mb-4">Contact Us</h1>
          <p className="text-xl mb-6 max-w-2xl mx-auto">
            Get in touch with our team for any inquiries or support
          </p>
          <div className="flex items-center justify-center space-x-2 text-white/80">
            <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>Contact</span>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <AnimatedSection>
              <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
                <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">Send Us a Message</h2>
                <p className="text-gray-600 mb-8">
                  Fill out the form below and our team will get back to you within 24 hours.
                </p>

                <form onSubmit={handleSubmit} id="contact-form" data-readdy-form>
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm"
                        placeholder="+1 (234) 567-890"
                      />
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                        Service Interest *
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 text-sm cursor-pointer"
                      >
                        <option value="">Select a service</option>
                        <option value="dental">Dental Laboratory Services</option>
                        <option value="medical">Medical Testing</option>
                        <option value="diagnostic">Clinical Diagnostics</option>
                        <option value="genetic">Genetic Testing</option>
                        <option value="research">Research & Development</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors duration-300 resize-none text-sm"
                        placeholder="Tell us about your needs..."
                      ></textarea>
                      <div className="text-right text-xs text-gray-500 mt-1">
                        {formData.message.length}/500 characters
                      </div>
                    </div>

                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-green-700">
                          <i className="ri-checkbox-circle-line text-xl"></i>
                          <span className="font-semibold">Message sent successfully! We'll get back to you soon.</span>
                        </div>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-red-700">
                          <i className="ri-error-warning-line text-xl"></i>
                          <span className="font-semibold">Something went wrong. Please try again.</span>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-8 py-4 bg-gradient-to-r from-primary-500 to-royal-600 text-white rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              </div>
            </AnimatedSection>

            {/* Contact Information */}
            <AnimatedSection delay={0.2}>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-primary-500 to-royal-600 rounded-xl">
                        <i className={`${info.icon} text-white text-2xl`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{info.title}</h3>
                        {info.link !== '#' ? (
                          <a
                            href={info.link}
                            className="text-gray-600 hover:text-primary-600 transition-colors duration-300"
                          >
                            {info.detail}
                          </a>
                        ) : (
                          <p className="text-gray-600">{info.detail}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* WhatsApp Contact */}
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-lg p-6 text-white">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-white/20 rounded-xl">
                      <i className="ri-whatsapp-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">WhatsApp Support</h3>
                      <p className="text-white/90 text-sm">Quick response guaranteed</p>
                    </div>
                  </div>
                  <a
                    href="https://wa.me/1234567890"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full px-6 py-3 bg-white text-green-600 rounded-lg text-center font-semibold hover:bg-green-50 transition-all duration-300 whitespace-nowrap"
                  >
                    Chat on WhatsApp
                  </a>
                </div>

                {/* Map */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.119763973046!3d40.69766374874431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY%2C%20USA!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="MedLab Pro Location"
                    className="rounded-2xl"
                  ></iframe>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <AnimatedSection className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Quick answers to common questions about our services
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'What are your turnaround times for test results?',
                answer: 'Standard tests are typically completed within 24-48 hours. We also offer express services with same-day results for urgent cases. Specialized tests may take 3-5 business days.',
              },
              {
                question: 'Do you accept insurance?',
                answer: 'Yes, we accept most major insurance plans. Please contact our billing department to verify your specific coverage and benefits.',
              },
              {
                question: 'How do I request a quote for services?',
                answer: 'You can request a quote by filling out our online form, calling our office, or sending us an email. Our team will respond within 24 hours with detailed pricing information.',
              },
              {
                question: 'Are your laboratory services certified?',
                answer: 'Yes, we are ISO 9001 certified, FDA approved, CAP accredited, and CLIA licensed. We maintain the highest standards of quality and compliance.',
              },
            ].map((faq, index) => (
              <details
                key={index}
                className="group bg-gray-50 rounded-2xl overflow-hidden hover:bg-gray-100 transition-colors duration-300"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <span className="text-lg font-bold text-gray-900 pr-4">{faq.question}</span>
                  <i className="ri-add-line text-2xl text-primary-600 group-open:rotate-45 transition-transform duration-300"></i>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/faq"
              className="inline-flex items-center space-x-2 text-primary-600 font-semibold hover:text-royal-600 transition-colors duration-300"
            >
              <span>View All FAQs</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </div>
  );
};

export default ContactPage;
